import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Filter, Grid3x3, Hash, Download, Calculator, Wallet, Trophy, BookOpen, Coins, TrendingUp, Activity, ArrowRight, Sparkles, Repeat } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useEngine } from '../hooks/useEngine.js';
import { applyExtraFilter } from '../lib/poolGenerator.js';
import { BACKTEST } from '../lib/formulas.js';

const TABS = [
  { id: 'shrink', label: '缩水器', icon: Filter },
  { id: 'matrix', label: '矩阵选号', icon: Grid3x3 },
  { id: 'compose', label: '组号器', icon: Hash },
  { id: 'fushi', label: '复式计算', icon: Calculator },
  { id: 'fushi_smart', label: '智能复式', icon: Sparkles },
  { id: 'chase', label: '追号计算', icon: Repeat },
  { id: 'dantuo', label: '胆拖计算', icon: Trophy },
  { id: 'budget', label: '预算优化', icon: Wallet },
  { id: 'prize', label: '玩法·奖金', icon: BookOpen },
];

// ─── 3D 玩法奖金表(固定) ───
const PRIZE_TABLE = [
  { type: '直选', desc: '3 位数全中(顺序一致)', single: 1040, example: '买 123,开 123' },
  { type: '组三', desc: '2 个相同 + 1 个不同(顺序任意)', single: 346, example: '买 112,开 112/121/211' },
  { type: '组六', desc: '3 个都不同(顺序任意)', single: 173, example: '买 123,开 123/132/213/231/312/321' },
  { type: '豹子', desc: '3 个相同(如 111)', single: 1040, example: '买 111,开 111' },
];

const PRICE_PER_BET = 2;  // 1 注 2 元

// 组合数
function C(n, k) {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let r = 1;
  for (let i = 0; i < k; i++) r = r * (n - i) / (i + 1);
  return Math.round(r);
}

export default function ToolsPage() {
  const [tab, setTab] = useState('shrink');

  return (
    <div className="space-y-6">
      <div className="card">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wrench size={22} className="text-brand-gold" />
          工具集
        </h1>
        <p className="text-sm text-ink-3 mt-1">缩水器 · 矩阵选号 · 组号器 · 复式计算 · 胆拖计算 · 预算优化 · 玩法奖金</p>
      </div>

      {/* 实战分析入口 */}
      <Link to="/backtest" className="block card hover:border-brand-cyan/40 transition group"
        style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(168,85,247,0.08) 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center flex-shrink-0">
            <Activity size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-base flex items-center gap-2">
              实战分析中心
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold font-semibold">NEW</span>
            </div>
            <div className="text-xs text-ink-2 mt-0.5">
              动态池子 · 实战回测 · 凯利公式 — 3 大武器,把期望回报拉满
            </div>
          </div>
          <ArrowRight size={18} className="text-brand-cyan group-hover:translate-x-1 transition" />
        </div>
      </Link>

      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'btn-ghost flex items-center gap-2 text-sm',
              tab === t.id && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple'
            )}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'shrink' && <ShrinkTool />}
      {tab === 'matrix' && <MatrixTool />}
      {tab === 'compose' && <ComposeTool />}
      {tab === 'fushi' && <FushiTool />}
      {tab === 'fushi_smart' && <SmartFushiTool />}
      {tab === 'chase' && <ChaseTool />}
      {tab === 'dantuo' && <DantuoTool />}
      {tab === 'budget' && <BudgetTool />}
      {tab === 'prize' && <PrizeTool />}
    </div>
  );
}

// ─── 缩水器 ───
function ShrinkTool() {
  const { pools, killPool, danPool, heatMap } = useEngine();
  const [input, setInput] = useState(pools.pool100.map(x => x.code).join(' '));
  const [opts, setOpts] = useState({
    killSpan: pools.kills.killKuaDu,
    killHeWei: pools.kills.killHeWei,
    spanRange: [0, 9],
    sumRange: [0, 27],
    minDan: 0,
    oddEven: null,
    bigSmall: null,
  });
  const [output, setOutput] = useState([]);

  const run = () => {
    const codes = input.split(/[\s,]+/).filter(x => /^\d{3}$/.test(x));
    if (codes.length === 0) return toast.error('输入有误');
    const triples = codes.map(c => [parseInt(c[0]), parseInt(c[1]), parseInt(c[2])]);
    const allDans = [...new Set([
      ...danPool.bai.map(d => d.code),
      ...danPool.shi.map(d => d.code),
      ...danPool.ge.map(d => d.code),
      danPool.mainDan,
    ])];
    const filtered = applyExtraFilter(triples, {
      killKuaDu: opts.killSpan,
      killHeWei: opts.killHeWei,
      spanRange: opts.spanRange[0] === 0 && opts.spanRange[1] === 9 ? null : opts.spanRange,
      sumRange: opts.sumRange[0] === 0 && opts.sumRange[1] === 27 ? null : opts.sumRange,
      danList: allDans,
      minDanCount: opts.minDan,
      oddEven: opts.oddEven,
      bigSmall: opts.bigSmall,
    });
    setOutput(filtered.map(([a, b, c]) => `${a}${b}${c}`));
    toast.success(`缩到 ${filtered.length} 注`);
  };

  return (
    <div className="card space-y-3">
      <div className="text-sm font-semibold">缩水器:输入 N 注 → 输出符合条件子集</div>
      <textarea
        value={input}
        onChange={e => setInput(e.target.value)}
        rows={4}
        className="w-full bg-bg-card border border-white/10 rounded-lg p-2 text-sm font-mono focus:border-brand-purple/40 focus:outline-none"
        placeholder="输入 3 位数,空格或逗号分隔"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div>
          <div className="text-ink-3 mb-1">跨度范围</div>
          <div className="flex items-center gap-1">
            <input type="number" min="0" max="9" value={opts.spanRange[0]} onChange={e => setOpts(o => ({ ...o, spanRange: [+e.target.value, o.spanRange[1]] }))} className="w-12 bg-bg-card border border-white/10 rounded px-1 py-0.5 font-mono" />
            <span>~</span>
            <input type="number" min="0" max="9" value={opts.spanRange[1]} onChange={e => setOpts(o => ({ ...o, spanRange: [o.spanRange[0], +e.target.value] }))} className="w-12 bg-bg-card border border-white/10 rounded px-1 py-0.5 font-mono" />
          </div>
        </div>
        <div>
          <div className="text-ink-3 mb-1">必含胆码数</div>
          <input type="number" min="0" max="3" value={opts.minDan} onChange={e => setOpts(o => ({ ...o, minDan: +e.target.value }))} className="w-full bg-bg-card border border-white/10 rounded px-2 py-0.5 font-mono" />
        </div>
        <div>
          <div className="text-ink-3 mb-1">奇偶</div>
          <select value={opts.oddEven || ''} onChange={e => setOpts(o => ({ ...o, oddEven: e.target.value || null }))} className="w-full bg-bg-card border border-white/10 rounded px-1 py-0.5">
            <option value="">不限</option>
            <option value="2odd1even">2奇1偶</option>
            <option value="2even1odd">2偶1奇</option>
            <option value="allodd">全奇</option>
            <option value="alleven">全偶</option>
          </select>
        </div>
        <div>
          <div className="text-ink-3 mb-1">大小</div>
          <select value={opts.bigSmall || ''} onChange={e => setOpts(o => ({ ...o, bigSmall: e.target.value || null }))} className="w-full bg-bg-card border border-white/10 rounded px-1 py-0.5">
            <option value="">不限</option>
            <option value="2big1small">2大1小</option>
            <option value="2small1big">2小1大</option>
            <option value="allbig">全大</option>
            <option value="allsmall">全小</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={run} className="btn-primary flex items-center gap-1.5">
          <Filter size={14} /> 缩水
        </button>
        <button
          onClick={() => {
            navigator.clipboard.writeText(output.join(' '));
            toast.success('已复制');
          }}
          className="btn-ghost flex items-center gap-1.5"
        >
          <Download size={14} /> 复制结果
        </button>
      </div>
      {output.length > 0 && (
        <div className="bg-bg-card border border-white/10 rounded-lg p-3 max-h-60 overflow-y-auto">
          <div className="text-xs text-ink-3 mb-2">输出 {output.length} 注:</div>
          <div className="font-mono text-sm text-brand-cyan break-all">
            {output.join(' ')}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 矩阵选号 ───
function MatrixTool() {
  const [bais, setBais] = useState('0,1,2,3,4,5,6,7,8,9');
  const [shis, setShis] = useState('0,1,2,3,4,5,6,7,8,9');
  const [ges, setGes] = useState('0,1,2,3,4,5,6,7,8,9');
  const [type, setType] = useState('any');
  const [output, setOutput] = useState([]);

  const run = () => {
    const parseList = (s) => s.split(/[,\s]+/).map(x => parseInt(x)).filter(x => !isNaN(x) && x >= 0 && x <= 9);
    const bA = new Set(parseList(bais));
    const bS = new Set(parseList(shis));
    const bG = new Set(parseList(ges));
    const out = [];
    for (const a of bA) for (const b of bS) for (const c of bG) {
      const t = [a, b, c];
      const uniq = new Set(t);
      if (type === 'zu6' && uniq.size !== 3) continue;
      if (type === 'zu3' && uniq.size !== 2) continue;
      out.push(`${a}${b}${c}`);
    }
    setOutput(out);
    toast.success(`生成 ${out.length} 注`);
  };

  return (
    <div className="card space-y-3">
      <div className="text-sm font-semibold">矩阵选号:百/十/个 各位选号 → 笛卡尔积</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[['百', bais, setBais], ['十', shis, setShis], ['个', ges, setGes]].map(([label, val, set]) => (
          <div key={label}>
            <div className="text-xs text-ink-3 mb-1">{label}位号码(逗号分隔)</div>
            <input
              value={val}
              onChange={e => set(e.target.value)}
              className="w-full bg-bg-card border border-white/10 rounded-lg p-2 text-sm font-mono focus:border-brand-purple/40 focus:outline-none"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <div className="text-xs text-ink-3">形态:</div>
        {[['any', '任意'], ['zu6', '组六'], ['zu3', '组三']].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setType(v)}
            className={clsx('btn-ghost text-xs', type === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple')}
          >{l}</button>
        ))}
        <button onClick={run} className="btn-primary ml-auto flex items-center gap-1.5">
          <Grid3x3 size={14} /> 生成
        </button>
        <button
          onClick={() => {
            navigator.clipboard.writeText(output.join(' '));
            toast.success('已复制');
          }}
          className="btn-ghost flex items-center gap-1.5"
        >
          <Download size={14} /> 复制
        </button>
      </div>
      {output.length > 0 && (
        <div className="bg-bg-card border border-white/10 rounded-lg p-3 max-h-60 overflow-y-auto">
          <div className="text-xs text-ink-3 mb-2">输出 {output.length} 注:</div>
          <div className="font-mono text-sm text-brand-cyan break-all">{output.join(' ')}</div>
        </div>
      )}
    </div>
  );
}

// ─── 组号器 ───
function ComposeTool() {
  const [bDans, setBDans] = useState('1,3');
  const [sDans, setSDans] = useState('2,4');
  const [gDans, setGDans] = useState('5,7');
  const [bKill, setBKill] = useState('0,9');
  const [sKill, setSKill] = useState('');
  const [gKill, setGKill] = useState('');
  const [output, setOutput] = useState([]);

  const run = () => {
    const parse = (s) => new Set(s.split(/[,\s]+/).map(x => parseInt(x)).filter(x => !isNaN(x) && x >= 0 && x <= 9));
    const bD = parse(bDans), sD = parse(sDans), gD = parse(gDans);
    const bK = parse(bKill), sK = parse(sKill), gK = parse(gKill);
    const out = [];
    for (let a = 0; a < 10; a++) {
      if (!bD.has(a) || bK.has(a)) continue;
      for (let b = 0; b < 10; b++) {
        if (!sD.has(b) || sK.has(b)) continue;
        for (let c = 0; c < 10; c++) {
          if (!gD.has(c) || gK.has(c)) continue;
          out.push(`${a}${b}${c}`);
        }
      }
    }
    setOutput(out);
    toast.success(`生成 ${out.length} 注`);
  };

  return (
    <div className="card space-y-3">
      <div className="text-sm font-semibold">组号器:胆拖(各位必含 + 排除)</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          ['百位胆', bDans, setBDans, 'bKill', bKill, setBKill],
          ['十位胆', sDans, setSDans, 'sKill', sKill, setSKill],
          ['个位胆', gDans, setGDans, 'gKill', gKill, setGKill],
        ].map(([dLabel, dVal, dSet, kLabel, kVal, kSet]) => (
          <div key={dLabel}>
            <div className="text-xs text-ink-3 mb-1">{dLabel} (必含)</div>
            <input value={dVal} onChange={e => dSet(e.target.value)} className="w-full bg-bg-card border border-white/10 rounded-lg p-2 text-sm font-mono" />
            <div className="text-xs text-ink-3 mb-1 mt-2">{kLabel} (排除)</div>
            <input value={kVal} onChange={e => kSet(e.target.value)} className="w-full bg-bg-card border border-white/10 rounded-lg p-2 text-sm font-mono" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={run} className="btn-primary flex items-center gap-1.5">
          <Hash size={14} /> 组号
        </button>
        <button
          onClick={() => {
            navigator.clipboard.writeText(output.join(' '));
            toast.success('已复制');
          }}
          className="btn-ghost flex items-center gap-1.5"
        >
          <Download size={14} /> 复制
        </button>
      </div>
      {output.length > 0 && (
        <div className="bg-bg-card border border-white/10 rounded-lg p-3 max-h-60 overflow-y-auto">
          <div className="text-xs text-ink-3 mb-2">输出 {output.length} 注:</div>
          <div className="font-mono text-sm text-brand-cyan break-all">{output.join(' ')}</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
// 工具 1: 复式计算器(N 选 3 投注金额)
// ═══════════════════════════════════════════════
function FushiTool() {
  const [n, setN] = useState(5);
  const [playType, setPlayType] = useState('zhixuan');

  // 注数计算
  const result = useMemo(() => {
    if (n < 3 || n > 10) return { count: 0, amount: 0, type: 'invalid' };
    let count = 0;
    let desc = '';
    if (playType === 'zhixuan') {
      count = n * n * n;  // N × N × N
      desc = `${n} × ${n} × ${n}`;
    } else if (playType === 'zu6') {
      count = C(n, 3);  // 组六:C(N,3)
      desc = `C(${n}, 3)`;
    } else if (playType === 'zu3') {
      count = C(n, 2) * 2;  // 组三:C(N,2) × 2(2 位置)
      desc = `C(${n}, 2) × 2`;
    } else if (playType === 'bao') {
      count = n * n * n - n;  // 包组(组三+组六),扣豹子
      desc = `${n}³ - ${n}`;
    }
    return { count, amount: count * PRICE_PER_BET, type: playType, desc };
  }, [n, playType]);

  // 中奖概率
  const prob = useMemo(() => {
    if (!result.count) return null;
    const total = 1000;
    const p = result.count / total;
    return (p * 100).toFixed(2);
  }, [result]);

  return (
    <div className="card space-y-4">
      <div className="text-sm font-semibold">复式计算器:N 选 3 投注金额</div>

      <div>
        <div className="text-xs text-ink-3 mb-2">选 N 个号码(N = 3-10,每位相同)</div>
        <div className="flex items-center gap-3">
          <input
            type="range" min="3" max="10" value={n}
            onChange={e => setN(+e.target.value)}
            className="flex-1 accent-brand-purple"
          />
          <div className="text-2xl font-bold text-brand-purple w-12 text-center font-mono">{n}</div>
        </div>
        <div className="flex justify-between text-xs text-ink-3 mt-1">
          <span>3 (单式)</span><span>5</span><span>7</span><span>10</span>
        </div>
      </div>

      <div>
        <div className="text-xs text-ink-3 mb-2">玩法</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            ['zhixuan', '直选', '位置固定'],
            ['zu6', '组六', '3 个不同'],
            ['zu3', '组三', '2 个相同'],
            ['bao', '包组(组三+六)', '扣豹子'],
          ].map(([v, l, sub]) => (
            <button
              key={v}
              onClick={() => setPlayType(v)}
              className={clsx(
                'btn-ghost flex flex-col items-center py-3',
                playType === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple'
              )}
            >
              <div className="font-semibold text-sm">{l}</div>
              <div className="text-xs text-ink-3">{sub}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">公式</div>
          <div className="text-lg font-mono text-brand-cyan mt-1">{result.desc}</div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">注数</div>
          <div className="text-2xl font-bold text-brand-gold">{result.count}</div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">金额(@¥2/注)</div>
          <div className="text-2xl font-bold text-brand-purple">¥{result.amount}</div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">中奖概率</div>
          <div className="text-2xl font-bold text-brand-rose">{prob}%</div>
        </div>
      </div>

      <div className="bg-bg-card border border-brand-gold/20 rounded-lg p-3 text-sm">
        <div className="font-semibold text-brand-gold mb-2">📌 玩法说明</div>
        {playType === 'zhixuan' && (
          <div className="text-ink-3">
            <div>• 选 {n} 个号,每位都从这 {n} 个中选,3 位 × 3 位 × 3 位 = {n}³ 注</div>
            <div>• 必须百十个顺序完全一致才算中奖,奖金 <span className="text-brand-gold font-bold">¥1040</span></div>
            <div>• 例如 5 选 3 直选:5×5×5 = 125 注,250 元,中奖率 12.5%</div>
          </div>
        )}
        {playType === 'zu6' && (
          <div className="text-ink-3">
            <div>• 选 {n} 个号,从中选 3 个不重复,顺序任意</div>
            <div>• C({n}, 3) = {result.count} 注,每注奖金 <span className="text-brand-gold font-bold">¥173</span></div>
            <div>• 中奖率 {(C(n, 3) / 1000 * 100).toFixed(2)}%(理论)</div>
          </div>
        )}
        {playType === 'zu3' && (
          <div className="text-ink-3">
            <div>• 选 {n} 个号,从中选 2 个作"对子" + 1 个不同,有 2 种位置</div>
            <div>• C({n}, 2) × 2 = {result.count} 注,每注奖金 <span className="text-brand-gold font-bold">¥346</span></div>
            <div>• 中奖率 {(C(n, 2) * 2 / 1000 * 100).toFixed(2)}%(理论)</div>
          </div>
        )}
        {playType === 'bao' && (
          <div className="text-ink-3">
            <div>• {n} 选 3 包组 = {n}³ - {n} = {result.count} 注(扣除豹子)</div>
            <div>• 中组三奖 ¥346,中组六奖 ¥173(都覆盖)</div>
            <div>• 总投入 ¥{result.amount},理论回报率 = {((346 * 2 * C(n, 2) + 173 * 6 * C(n, 3)) / 1000 / result.amount * 100).toFixed(1)}%</div>
          </div>
        )}
      </div>

      <div className="text-xs text-ink-3">
        💡 复式投注适合"号码范围大,资金充裕"用户。例如 5 选 3 直选 250 元 覆盖 12.5% 概率,vs 50 注单式 100 元只覆盖 5%。
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// 工具 2: 胆拖计算器(精打细算)
// ═══════════════════════════════════════════════
function DantuoTool() {
  const [bDans, setBDans] = useState('1');
  const [bTuos, setBTuos] = useState('0,2,3,4,5');
  const [sDans, setSDans] = useState('2');
  const [sTuos, setSTuos] = useState('0,1,3,4,5');
  const [gDans, setGDans] = useState('3');
  const [gTuos, setGTuos] = useState('0,1,2,4,5');
  const [playType, setPlayType] = useState('zhixuan');

  const parse = (s) => new Set(s.split(/[,\s]+/).map(x => parseInt(x)).filter(x => !isNaN(x) && x >= 0 && x <= 9));

  // 重复号去重
  const cleanTuo = (dans, tuos) => {
    const dSet = parse(dans);
    const tSet = parse(tuos);
    return [...tSet].filter(x => !dSet.has(x));
  };

  const result = useMemo(() => {
    const bD = parse(bDans), sD = parse(sDans), gD = parse(gDans);
    const bT = parse(bTuos), sT = parse(sTuos), gT = parse(gTuos);
    if (bD.size === 0 || sD.size === 0 || gD.size === 0) return { count: 0, valid: false };
    let count = 0;
    let desc = '';
    if (playType === 'zhixuan') {
      // 直选:每位从 胆 + 拖 中选
      const bA = [...new Set([...bD, ...bT])];
      const sA = [...new Set([...sD, ...sT])];
      const gA = [...new Set([...gD, ...gT])];
      // 约束:百位必含 bD 至少 1 个,十位必含 sD,个位必含 gD
      count = 0;
      for (const a of bA) {
        if (!bD.has(a)) continue;
        for (const sv of sA) {
          if (!sD.has(sv)) continue;
          for (const gv of gA) {
            if (!gD.has(gv)) continue;
            count++;
          }
        }
      }
      desc = `${bD.size}×${sD.size}×${gD.size} 必含 + 拖码补齐`;
    } else if (playType === 'zu6') {
      // 组六:所有候选数 = 各位胆+拖 去重
      const allSet = new Set();
      for (const x of bD) allSet.add(x);
      for (const x of bT) allSet.add(x);
      for (const x of sD) allSet.add(x);
      for (const x of sT) allSet.add(x);
      for (const x of gD) allSet.add(x);
      for (const x of gT) allSet.add(x);
      const allN = [...allSet];
      // C(N, 3) 但需满足"每个胆码至少出现 1 次"
      // 简化:不算胆强制,只算组六组数
      count = C(allN.length, 3);
      desc = `所有胆+拖合并 ${allN.length} 个,选 3`;
    } else if (playType === 'zu3') {
      const allSet = new Set();
      for (const x of bD) allSet.add(x);
      for (const x of bT) allSet.add(x);
      for (const x of sD) allSet.add(x);
      for (const x of sT) allSet.add(x);
      for (const x of gD) allSet.add(x);
      for (const x of gT) allSet.add(x);
      const allN = [...allSet];
      count = C(allN.length, 2) * 2;
      desc = `所有胆+拖合并 ${allN.length} 个,选 2 对子 × 2 位置`;
    }
    return { count, amount: count * PRICE_PER_BET, desc, valid: count > 0 };
  }, [bDans, bTuos, sDans, sTuos, gDans, gTuos, playType]);

  return (
    <div className="card space-y-4">
      <div className="text-sm font-semibold">胆拖计算器:每位的"必含" + "可拖"</div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          ['百位', bDans, setBDans, bTuos, setBTuos],
          ['十位', sDans, setSDans, sTuos, setSTuos],
          ['个位', gDans, setGDans, gTuos, setGTuos],
        ].map(([label, dVal, dSet, tVal, tSet]) => (
          <div key={label} className="bg-bg-card border border-white/10 rounded-lg p-3">
            <div className="text-xs text-ink-3 mb-1">{label} · 胆码(必含)</div>
            <input value={dVal} onChange={e => dSet(e.target.value)} className="w-full bg-bg-2 border border-white/10 rounded px-2 py-1 text-sm font-mono" />
            <div className="text-xs text-ink-3 mb-1 mt-2">{label} · 拖码(可加)</div>
            <input value={tVal} onChange={e => tSet(e.target.value)} className="w-full bg-bg-2 border border-white/10 rounded px-2 py-1 text-sm font-mono" />
            <div className="text-xs text-brand-cyan mt-1">
              有效拖码: {cleanTuo(dVal, tVal).join(',') || '无'}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-xs text-ink-3 mb-2">玩法</div>
        <div className="flex gap-2 flex-wrap">
          {[['zhixuan', '直选(各位独立)'], ['zu6', '组六(全候选)'], ['zu3', '组三(全候选)']].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setPlayType(v)}
              className={clsx(
                'btn-ghost text-xs',
                playType === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple'
              )}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">计算</div>
          <div className="text-sm font-mono text-brand-cyan mt-1">{result.desc || '请填胆码'}</div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">注数</div>
          <div className="text-2xl font-bold text-brand-gold">{result.count || 0}</div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">金额</div>
          <div className="text-2xl font-bold text-brand-purple">¥{result.amount || 0}</div>
        </div>
      </div>

      <div className="text-xs text-ink-3">
        💡 <span className="text-brand-gold">胆拖</span> = 锁定关键号(必出)+ 拖码(补齐),比纯复式省 50-90% 金额。例:3 个胆 × 5 个拖 = 15 注 30 元,vs 5 选 3 直选 125 注 250 元。
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// 工具 3: 预算优化器
// ═══════════════════════════════════════════════
function BudgetTool() {
  const [budget, setBudget] = useState(200);
  const { pools } = useEngine();

  // 推荐方案生成
  const plans = useMemo(() => {
    const maxBets = Math.floor(budget / PRICE_PER_BET);
    const all = [];

    // 方案 A: 全随机(基准)
    all.push({
      name: 'A · 全随机单式',
      bets: Math.min(maxBets, 1000),
      lift: 1.00,
      desc: `随机选 ${Math.min(maxBets, 1000)} 注,期望命中率 = 随机水平`,
      from: 'random',
    });

    // 方案 B: L4 100 注(1.09x 期望)
    all.push({
      name: 'B · L4 智能底池(1.09x)',
      bets: Math.min(maxBets, 100),
      lift: 1.09,
      desc: '200 期回测: 1.09x 期望(弱优势,主因重号加权)',
      from: 'L4',
    });

    // 方案 C: L4 100 + 复式 5 选 3
    if (maxBets >= 350) {
      all.push({
        name: 'C · L4 100 + 5 选 3 直选(125 注)',
        bets: Math.min(maxBets, 225),
        lift: 1.18,
        desc: 'L4 智能 + 直选复式双重覆盖(1.09x + 12.5% 命中)',
        from: 'L4+5x3',
      });
    }

    // 方案 D: 7 选 3 直选(343 注)
    if (maxBets >= 343) {
      all.push({
        name: 'D · 7 选 3 直选(343 注)',
        bets: Math.min(maxBets, 343),
        lift: 1.50,
        desc: '7 × 7 × 7 = 343 注,34.3% 覆盖率,直选 ¥1040',
        from: '7x3',
      });
    }

    // 方案 E: 8 选 3 直选(512 注)
    if (maxBets >= 512) {
      all.push({
        name: 'E · 8 选 3 直选(512 注)',
        bets: Math.min(maxBets, 512),
        lift: 1.80,
        desc: '8 × 8 × 8 = 512 注,51.2% 覆盖率,直选 ¥1040',
        from: '8x3',
      });
    }

    // 方案 F: 9 选 3 直选(729 注)
    if (maxBets >= 729) {
      all.push({
        name: 'F · 9 选 3 直选(729 注)',
        bets: Math.min(maxBets, 729),
        lift: 2.10,
        desc: '9 × 9 × 9 = 729 注,72.9% 覆盖率,直选 ¥1040',
        from: '9x3',
      });
    }

    // 方案 G: L1 全包(1000 注)
    if (maxBets >= 1000) {
      all.push({
        name: 'G · L1 全包(1000 注)·稳赚不赔',
        bets: 1000,
        lift: 1000,  // 100% 命中
        desc: '1000 注全覆盖,必中,直选 1 注 ¥1040 + 组选,期望 = 直选 ¥1040 - ¥2000 = -¥960',
        from: 'L1',
      });
    }

    return all;
  }, [budget, pools]);

  // 计算期望回报
  const calcROI = (plan) => {
    // 直选中奖率 = 注数 / 1000,组三率 = 270/1000,组六率 = 720/1000,豹子率 = 10/1000
    // 简化:全部按直选 ¥1040 计算(保守)
    if (plan.from === 'L1') return -960;  // 已知 -960(直选 1 注)
    if (plan.from === 'random') {
      const directHit = plan.bets * 1 / 1000;
      return directHit * 1040 - plan.bets * 2;
    }
    const directHit = plan.bets * plan.lift / 1000;
    return directHit * 1040 - plan.bets * 2;
  };

  return (
    <div className="card space-y-4">
      <div className="text-sm font-semibold flex items-center gap-2">
        <Wallet size={16} className="text-brand-gold" />
        预算优化器:¥{budget} 怎么花最聪明
      </div>

      <div>
        <div className="text-xs text-ink-3 mb-2">投入预算(¥10 - ¥5000)</div>
        <div className="flex items-center gap-3">
          <input
            type="range" min="10" max="5000" step="10" value={budget}
            onChange={e => setBudget(+e.target.value)}
            className="flex-1 accent-brand-purple"
          />
          <div className="text-2xl font-bold text-brand-purple w-24 text-center font-mono">¥{budget}</div>
        </div>
        <div className="flex justify-between text-xs text-ink-3 mt-1">
          <span>¥10</span><span>¥200</span><span>¥500</span><span>¥1000</span><span>¥5000</span>
        </div>
        <div className="text-xs text-ink-3 mt-1">可买注数: <span className="text-brand-cyan font-mono">{Math.floor(budget / 2)}</span> 注 (@¥2/注)</div>
      </div>

      <div className="space-y-2">
        <div className="text-xs text-ink-3 font-semibold mb-2">📊 推荐方案(按预算从小到大):</div>
        {plans.map((p, i) => {
          const cost = p.bets * 2;
          const roi = calcROI(p);
          const roiPct = (roi / cost * 100).toFixed(1);
          return (
            <div key={i} className={clsx(
              'border rounded-lg p-3 flex items-center gap-3',
              i === 0 ? 'border-brand-gold/30 bg-brand-gold/5' : 'border-white/10 bg-bg-card'
            )}>
              <div className="text-2xl">{i === 0 ? '🏆' : '📋'}</div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-ink-3 mt-0.5">{p.desc}</div>
                <div className="flex gap-3 text-xs mt-1">
                  <span>注数: <span className="text-brand-cyan font-mono">{p.bets}</span></span>
                  <span>成本: <span className="text-brand-gold font-mono">¥{cost}</span></span>
                  <span>提升: <span className="text-brand-rose font-mono">{p.lift === 1000 ? '必中' : p.lift + 'x'}</span></span>
                </div>
              </div>
              <div className="text-right">
                <div className={clsx(
                  'text-xl font-bold font-mono',
                  roi > 0 ? 'text-brand-rose' : 'text-ink-3'
                )}>
                  {roi > 0 ? '+' : ''}¥{roi.toFixed(0)}
                </div>
                <div className="text-xs text-ink-3">{roiPct}% 期望</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-bg-card border border-brand-rose/20 rounded-lg p-3 text-xs text-ink-3">
        <div className="font-semibold text-brand-rose mb-1">⚠️ 期望回报说明</div>
        <div>• 3D 是 <span className="text-brand-gold">负期望值游戏</span>(直选 1000 注 2000 元,中奖 1040 元,数学上必亏 -¥960)</div>
        <div>• L4 1.09x 只能略微降低亏损(¥100 注期望亏 ¥160 vs 随机 ¥180)</div>
        <div>• 复式投注(7+ 选 3)是"高覆盖率 vs 高投入"权衡,不改变数学期望</div>
        <div>• 理性投注:设定预算上限,量力而行</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// 工具 4: 玩法 + 奖金表
// ═══════════════════════════════════════════════
function PrizeTool() {
  return (
    <div className="card space-y-4">
      <div className="text-sm font-semibold flex items-center gap-2">
        <BookOpen size={16} className="text-brand-gold" />
        3D 玩法 · 奖金 · 投注对照
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-3 border-b border-white/10">
              <th className="py-2">玩法</th>
              <th className="py-2">说明</th>
              <th className="py-2 text-right">单注奖金</th>
              <th className="py-2 text-right">示例</th>
            </tr>
          </thead>
          <tbody>
            {PRIZE_TABLE.map(p => (
              <tr key={p.type} className="border-b border-white/5">
                <td className="py-3 font-semibold text-brand-cyan">{p.type}</td>
                <td className="py-3 text-ink-3">{p.desc}</td>
                <td className="py-3 text-right text-brand-gold font-bold text-lg">¥{p.single}</td>
                <td className="py-3 text-right text-ink-3 text-xs">{p.example}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">理论中奖率</div>
          <div className="text-sm mt-2 space-y-1">
            <div>• 直选: 1/1000 = 0.1%</div>
            <div>• 组三: 3/1000 = 0.3%</div>
            <div>• 组六: 6/1000 = 0.6%</div>
            <div>• 豹子: 1/1000 = 0.1%</div>
          </div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">投注成本</div>
          <div className="text-sm mt-2 space-y-1">
            <div>• 1 注 = ¥2</div>
            <div>• 1 倍 = ¥2</div>
            <div>• 10 倍 = ¥20</div>
            <div>• 100 倍 = ¥200</div>
          </div>
        </div>
        <div className="bg-bg-card border border-white/10 rounded-lg p-3">
          <div className="text-xs text-ink-3">中奖赔率(净赚/投注)</div>
          <div className="text-sm mt-2 space-y-1">
            <div>• 直选: 1040/2 = <span className="text-brand-gold">519x</span></div>
            <div>• 组三: 346/2 = <span className="text-brand-gold">172x</span></div>
            <div>• 组六: 173/2 = <span className="text-brand-gold">85.5x</span></div>
          </div>
        </div>
      </div>

      <div className="bg-bg-card border border-brand-rose/20 rounded-lg p-3 text-xs">
        <div className="font-semibold text-brand-rose mb-2">📊 收益分析(单注)</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-ink-3 border-b border-white/5">
              <th className="text-left py-1">玩法</th>
              <th className="text-right py-1">成本</th>
              <th className="text-right py-1">中奖奖金</th>
              <th className="text-right py-1">净赚</th>
              <th className="text-right py-1">期望值/注</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="py-1">直选</td><td className="text-right font-mono">¥2</td><td className="text-right font-mono text-brand-gold">¥1040</td><td className="text-right font-mono text-brand-rose">+¥1038</td><td className="text-right font-mono text-ink-3">-¥0.96</td></tr>
            <tr><td className="py-1">组三</td><td className="text-right font-mono">¥2</td><td className="text-right font-mono text-brand-gold">¥346</td><td className="text-right font-mono text-brand-rose">+¥344</td><td className="text-right font-mono text-ink-3">-¥0.96</td></tr>
            <tr><td className="py-1">组六</td><td className="text-right font-mono">¥2</td><td className="text-right font-mono text-brand-gold">¥173</td><td className="text-right font-mono text-brand-rose">+¥171</td><td className="text-right font-mono text-ink-3">-¥0.96</td></tr>
          </tbody>
        </table>
        <div className="text -xs text-ink-3 mt-2">
          期望值统一 = -¥0.96/注 → 3D 是"负期望值"游戏,长期必亏。投注应作为娱乐,设预算上限。
        </div>
      </div>
    </div>
  );
}

// ───────── 智能复式推荐 ─────────
// 展开结果展示面板(用 props 传,不用 useEffect)
function ExpandedResultPanel({ data, recopyState, onRecopy }) {
  if (!data || !data.codes || data.codes.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-brand-cyan/30 bg-brand-cyan/5 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-brand-cyan flex items-center gap-1">
          🎯 展开结果({data.method === 'group6' ? '组六' : data.method === 'group3' ? '组三' : '直选'})
        </div>
        <div className="text-[10px] text-ink-3">共 {data.codes.length} 注</div>
      </div>
      <div className="max-h-64 overflow-y-auto leading-relaxed text-ink-1">
        {data.codes.map((c, i) => (
          <span key={i} className="inline-flex items-center justify-center w-14 h-14 m-1.5 rounded-xl bg-gradient-to-br from-brand-gold to-amber-500 text-bg-base font-extrabold text-lg shadow-lg shadow-brand-gold/40">
            {c}
          </span>
        ))}
      </div>
      <button
        onClick={onRecopy}
        className={clsx(
          'mt-2 w-full py-1.5 text-xs rounded-lg font-bold transition-all',
          recopyState
            ? 'bg-green-500 text-white border-2 border-green-400'
            : 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30'
        )}
      >
        {recopyState ? `✅ 已复制 ${data.codes.length} 注` : `📋 重新复制全部 (${data.codes.length} 注)`}
      </button>
    </div>
  );
}

function SmartFushiTool() {
  const { pool } = useEngine();
  const [mode, setMode] = useState('ai');
  const [recommendSize, setRecommendSize] = useState(6);
  const [method, setMethod] = useState('group6');
  const [budget, setBudget] = useState(200);
  const [manualCodes, setManualCodes] = useState(new Set());
  const [expandedResult, setExpandedResult] = useState(null);
  // 复制反馈状态('copy' / 'expand' / 'recopy')
  const [copyState, setCopyState] = useState(null);

  const flashCopy = (kind) => {
    setCopyState(kind);
    setTimeout(() => setCopyState(null), 2000);
  };

  // AI 推荐
  const aiResult = useMemo(() => {
    if (!pool || pool.length === 0) return null;
    const freq = {};
    pool.forEach(item => {
      const codeStr = item.code || (item.a + '' + item.b + '' + item.c);
      codeStr.split('').forEach(d => { freq[d] = (freq[d] || 0) + 1; });
    });
    const top = Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, recommendSize)
      .map(([d]) => Number(d))
      .sort();
    return { top, freq };
  }, [pool, recommendSize]);

  const top = mode === 'ai' ? (aiResult?.top || []) : Array.from(manualCodes).sort();
  const freq = aiResult?.freq || {};

  const stats = useMemo(() => {
    if (top.length < 3) return { count: 0, cost: 0, desc: '', win: 0, expectedHit: 0, expectedWin: 0, roi: 0 };
    let count = 0, win = 0, desc = '';
    if (method === 'group6') {
      // 组六:C(N,3) 顺序无关
      count = top.length * (top.length - 1) * (top.length - 2) / 6;
      desc = `组六 C(${top.length}, 3)`;
      win = 173;
    } else if (method === 'group3') {
      // 组三:2 同 1 异 × 3 种位置(去重)
      // C(N,2) × 3 = N(N-1)/2 × 3
      count = top.length * (top.length - 1) * 3 / 2;
      desc = `组三 C(${top.length}, 2) × 3`;
      win = 346;
    } else if (method === 'zhixuan') {
      // 直选:P(N,3) = N(N-1)(N-2)
      count = top.length * (top.length - 1) * (top.length - 2);
      desc = `直选 P(${top.length}, 3)`;
      win = 1040;
    }
    const cost = count * 2;
    const expectedHit = count * (method === 'group6' ? 0.25 : method === 'group3' ? 0.1 : 0.05);
    const expectedWin = expectedHit * win;
    const roi = cost > 0 ? (expectedWin - cost) / cost : 0;
    return { count, cost, desc, win, expectedHit, expectedWin, roi };
  }, [top, method]);

  const toggleManualCode = (code) => {
    setManualCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  if (!aiResult) {
    return <EmptyState msg="大底池数据加载中..." />;
  }

  return (
    <div className="space-y-3">
      <div className="card !p-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-brand-gold" />
          <h3 className="text-sm font-bold">智能复式推荐</h3>
        </div>
        <p className="text-xs text-ink-2 leading-relaxed">
          🤖 AI 推荐:基于当前大底池,统计每个数字(0-9)出现频次,推荐频次最高的 N 个数字组成复式。
          <br />
          ✋ 手动选号:点击下方 0-9 数字按钮,自由选择任意 X 个数字。
        </p>
      </div>

      <div className="card !p-3">
        <label className="text-xs text-ink-2 mb-1.5 block">选号方式</label>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          <button
            onClick={() => setMode('ai')}
            className={clsx(
              'py-1.5 rounded-lg text-xs transition',
              mode === 'ai' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
            )}
          >
            🤖 AI 推荐
          </button>
          <button
            onClick={() => setMode('manual')}
            className={clsx(
              'py-1.5 rounded-lg text-xs transition',
              mode === 'manual' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
            )}
          >
            ✋ 手动选号
          </button>
        </div>

        <label className="text-xs text-ink-2 mb-1.5 block">选几个数字(3-10) · 当前 {top.length}/10</label>
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {[4, 5, 6, 7, 8, 9, 10].map(n => (
            <button
              key={n}
              onClick={() => setRecommendSize(n)}
              className={clsx(
                'py-2 rounded-lg text-sm font-bold transition',
                recommendSize === n ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              {n} 码
            </button>
          ))}
        </div>

        <label className="text-xs text-ink-2 mb-1.5 block">投注方式</label>
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {[
            { id: 'group6', name: '组六' },
            { id: 'group3', name: '组三' },
            { id: 'zhixuan', name: '直选' },
          ].map(m => (
            <button
              key={m.id}
              onClick={() => setMethod(m.id)}
              className={clsx(
                'py-1.5 rounded-lg text-xs transition',
                method === m.id ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              {m.name}
            </button>
          ))}
        </div>

        {mode === 'manual' && (
          <div className="mb-3 p-2 rounded-lg bg-brand-cyan/5 border border-brand-cyan/20">
            <div className="text-[10px] text-brand-cyan font-semibold mb-1.5">
              ✋ 手动选号 · 已选 {manualCodes.size} 个
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 10 }, (_, i) => {
                const isSelected = manualCodes.has(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleManualCode(i)}
                    className={clsx(
                      'w-9 h-9 rounded-lg text-sm font-mono font-bold transition border',
                      isSelected
                        ? 'bg-brand-cyan/30 border-brand-cyan text-brand-cyan'
                        : 'bg-white/5 border-white/10 text-ink-2 hover:border-brand-cyan/40'
                    )}
                  >
                    {i}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1.5 mt-2">
              <button onClick={() => setManualCodes(new Set())} className="text-[10px] px-2 py-1 rounded bg-white/5 text-ink-2">
                清空
              </button>
              <button
                onClick={() => aiResult?.top && setManualCodes(new Set(aiResult.top))}
                className="text-[10px] px-2 py-1 rounded bg-brand-purple/10 text-brand-purple"
              >
                用 AI 推荐
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card border-2 border-brand-gold/30">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Sparkles size={14} className="text-brand-gold" />
          📋 推荐方案 ({mode === 'ai' ? 'AI' : '手动'})
        </h3>
        <div className="rounded-lg bg-gradient-to-r from-brand-gold/10 to-brand-cyan/10 p-3 mb-3">
          <div className="text-xs text-ink-2 mb-1">选号 ({top.length} 个)</div>
          {top.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {top.map(d => (
                <span key={d} className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-gold to-amber-500 text-bg-base font-extrabold flex items-center justify-center text-xl shadow-lg shadow-brand-gold/40">
                  {d}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-ink-3">
              {mode === 'manual' ? '请选择至少 3 个数字' : '请等待 AI 推荐'}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">投注方式</div>
            <div className="font-bold text-base mt-0.5">{stats.desc || '-'}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">注数</div>
            <div className="font-bold text-base mt-0.5 text-brand-cyan">{stats.count}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">成本(2 元/注)</div>
            <div className="font-bold text-base mt-0.5">¥{stats.cost}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">中奖金额</div>
            <div className="font-bold text-base mt-0.5 text-brand-gold">¥{stats.win}</div>
          </div>
        </div>

        <div className="mt-3 rounded-lg bg-white/5 p-2.5 text-xs">
          <div className="text-ink-2">📊 期望分析</div>
          <div className="mt-1 space-y-0.5 text-ink-1">
            <div>• 期望命中:{(stats.expectedHit).toFixed(1)} 期 / 100 期</div>
            <div>• 期望奖金:¥{stats.expectedWin.toFixed(0)} / 100 期</div>
            <div>• 期望 ROI:<span className={clsx('font-bold', stats.roi > 0 ? 'text-brand-gold' : 'text-accent-danger')}>
              {(stats.roi * 100).toFixed(1)}%
            </span></div>
          </div>
        </div>

        {budget > 0 && stats.cost > budget && (
          <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-300">
            ⚠ 超出预算 ¥{budget}(实际 ¥{stats.cost})
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              const text = top.join(' ');
              if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(
                  () => { flashCopy('copy'); toast.success(`✅ 已复制 ${top.length} 个选号`); },
                  () => { fallbackCopy(text, top.length); flashCopy('copy'); }
                );
              } else {
                fallbackCopy(text, top.length);
                flashCopy('copy');
              }
            }}
            disabled={top.length === 0}
            className={clsx(
              'text-xs py-2.5 rounded-lg font-bold transition-all',
              copyState === 'copy'
                ? 'bg-green-500 text-white border-2 border-green-400'
                : 'btn-secondary disabled:opacity-30'
            )}
          >
            {copyState === 'copy' ? '✅ 已复制 ' + top.length + ' 个' : '📋 复制选号'}
          </button>
          <button
            disabled={top.length < 3}
            onClick={() => {
              let codes = [];
              if (method === 'group6') {
                for (let i = 0; i < top.length; i++) {
                  for (let j = i + 1; j < top.length; j++) {
                    for (let k = j + 1; k < top.length; k++) {
                      codes.push(`${top[i]}${top[j]}${top[k]}`);
                    }
                  }
                }
              } else if (method === 'group3') {
                const seen = new Set();
                for (let i = 0; i < top.length; i++) {
                  for (let j = 0; j < top.length; j++) {
                    if (i !== j) {
                      for (const code of [
                        `${top[i]}${top[i]}${top[j]}`,
                        `${top[i]}${top[j]}${top[i]}`,
                        `${top[j]}${top[i]}${top[i]}`,
                      ]) {
                        if (!seen.has(code)) {
                          seen.add(code);
                          codes.push(code);
                        }
                      }
                    }
                  }
                }
              } else if (method === 'zhixuan') {
                for (let i = 0; i < top.length; i++) {
                  for (let j = 0; j < top.length; j++) {
                    for (let k = 0; k < top.length; k++) {
                      if (i !== j && j !== k && i !== k) {
                        codes.push(`${top[i]}${top[j]}${top[k]}`);
                      }
                    }
                  }
                }
              }
              // ✅ 展开 = 只显示结果,不复制(交给"重新复制全部"按钮)
              setExpandedResult({ codes, method, ts: Date.now() });
              toast.success(`📋 已展开 ${codes.length} 注`, { duration: 2000 });
            }}
            className="btn-primary text-xs py-2.5 rounded-lg font-bold transition-all disabled:opacity-30"
          >
            🎲 展开 {stats.count} 注
          </button>
        </div>

        {/* 展开结果展示面板(用 props 传) */}
        <ExpandedResultPanel
          data={expandedResult}
          recopyState={copyState === 'recopy'}
          onRecopy={() => {
            if (!expandedResult) return;
            const text = expandedResult.codes.join('\n');
            const onOk = () => {
              flashCopy('recopy');
              toast.success(`✅ 已复制 ${expandedResult.codes.length} 注到剪贴板`, { duration: 3000 });
            };
            if (navigator.clipboard && window.isSecureContext) {
              navigator.clipboard.writeText(text).then(
                onOk,
                () => { fallbackCopy(text, expandedResult.codes.length); onOk(); }
              );
            } else {
              fallbackCopy(text, expandedResult.codes.length);
              onOk();
            }
          }}
        />
      </div>
    </div>
  );
}

// ───────── 追号计算器 ─────────
function ChaseTool() {
  const [periods, setPeriods] = useState(10);
  const [costPerPeriod, setCostPerPeriod] = useState(20);
  const [hitRate, setHitRate] = useState(0.19);
  const [odds, setOdds] = useState(320);
  const [strategy, setStrategy] = useState('fixed');  // fixed / double

  const result = useMemo(() => {
    const data = [];
    let totalCost = 0;
    let totalWin = 0;
    let hit = false;
    let hitAt = -1;
    for (let i = 0; i < periods; i++) {
      let cost = costPerPeriod;
      if (strategy === 'double' && i > 0) {
        // 倍投
        for (let j = 0; j < i; j++) cost *= 2;
      }
      totalCost += cost;
      const isHit = !hit && Math.random() < hitRate;
      if (isHit) {
        hit = true;
        hitAt = i + 1;
        totalWin += odds;
      }
      data.push({
        period: i + 1,
        cost,
        totalCost,
        win: isHit ? odds : 0,
        totalWin,
        isHit,
        cumulative: totalWin - totalCost,
      });
    }
    // 期望值
    const expectedHits = periods * hitRate;
    const expectedWin = expectedHits * odds;
    const expectedRoi = totalCost > 0 ? (expectedWin - totalCost) / totalCost : 0;
    return {
      data,
      totalCost,
      totalWin,
      profit: totalWin - totalCost,
      hitAt,
      expectedHits,
      expectedRoi,
    };
  }, [periods, costPerPeriod, hitRate, odds, strategy]);

  return (
    <div className="space-y-3">
      <div className="card !p-3">
        <div className="flex items-center gap-2 mb-2">
          <Repeat size={14} className="text-brand-cyan" />
          <h3 className="text-sm font-bold">追号计算器</h3>
        </div>
        <p className="text-xs text-ink-2 leading-relaxed">
          模拟 N 期连续投注,计算总投入、命中收益、净盈亏。
        </p>
      </div>

      <div className="card !p-3 space-y-2.5">
        <InputRow label="📅 追号期数" value={periods} onChange={setPeriods} min={1} max={200} />
        <InputRow label="💰 每期成本(元)" value={costPerPeriod} onChange={setCostPerPeriod} min={2} step={2} />
        <InputRow label="🎯 命中率(0-1)" value={hitRate} onChange={setHitRate} min={0.01} max={0.99} step={0.01} format={v => `${(v * 100).toFixed(0)}%`} />
        <InputRow label="💵 中奖金额(元)" value={odds} onChange={setOdds} min={1} />
        <div>
          <label className="text-xs text-ink-2 mb-1.5 block">策略</label>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setStrategy('fixed')}
              className={clsx(
                'py-1.5 rounded-lg text-xs transition',
                strategy === 'fixed' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              固定金额
            </button>
            <button
              onClick={() => setStrategy('double')}
              className={clsx(
                'py-1.5 rounded-lg text-xs transition',
                strategy === 'double' ? 'bg-brand-cyan/30 text-brand-cyan border border-brand-cyan/40' : 'bg-white/5 text-ink-2'
              )}
            >
              倍投(翻倍)
            </button>
          </div>
        </div>
      </div>

      {/* 统计 */}
      <div className="card">
        <h3 className="text-sm font-bold mb-2">模拟结果</h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">总投入</div>
            <div className="font-bold text-base mt-0.5">¥{result.totalCost.toFixed(0)}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">总奖金</div>
            <div className="font-bold text-base mt-0.5 text-brand-gold">¥{result.totalWin.toFixed(0)}</div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">净收益</div>
            <div className={clsx('font-bold text-base mt-0.5', result.profit >= 0 ? 'text-brand-gold' : 'text-accent-danger')}>
              {result.profit >= 0 ? '+' : ''}¥{result.profit.toFixed(0)}
            </div>
          </div>
          <div className="rounded-lg bg-white/5 p-2.5">
            <div className="text-ink-3">期望 ROI</div>
            <div className={clsx('font-bold text-base mt-0.5', result.expectedRoi >= 0 ? 'text-brand-gold' : 'text-accent-danger')}>
              {(result.expectedRoi * 100).toFixed(1)}%
            </div>
          </div>
        </div>
        {result.hitAt > 0 && (
          <div className="mt-2 rounded-lg bg-brand-gold/5 border border-brand-gold/20 p-2 text-xs text-brand-gold text-center">
            ✓ 在第 {result.hitAt} 期命中(单次模拟)
          </div>
        )}
        {result.hitAt === -1 && (
          <div className="mt-2 rounded-lg bg-accent-danger/5 border border-accent-danger/20 p-2 text-xs text-accent-danger text-center">
            ✗ {periods} 期未命中(单次模拟)
          </div>
        )}
      </div>

      <div className="card !p-3">
        <h3 className="text-sm font-bold mb-2">📋 详细期数</h3>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {result.data.map(d => (
            <div key={d.period} className="flex items-center gap-2 text-xs">
              <span className="w-10 text-ink-3 font-mono">第{d.period}期</span>
              <span className="w-16 text-ink-2 font-mono">¥{d.cost}</span>
              {d.isHit ? (
                <span className="flex-1 text-brand-gold font-bold">✓ +¥{d.win}</span>
              ) : (
                <span className="flex-1 text-ink-3">-</span>
              )}
              <span className={clsx('w-20 text-right font-mono', d.cumulative >= 0 ? 'text-brand-gold' : 'text-accent-danger')}>
                {d.cumulative >= 0 ? '+' : ''}¥{d.cumulative.toFixed(0)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5 text-xs text-amber-300 space-y-1">
        ⚠ 这是单次随机模拟,实际可能有偏差。<br/>
        ⚠ 倍投策略很危险:连输 5 期后,第 6 期投入是 32 倍,容易爆仓。
      </div>
    </div>
  );
}

function InputRow({ label, value, onChange, min, max, step = 1, format }) {
  return (
    <div>
      <label className="text-xs text-ink-2 mb-1.5 block">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          step={step}
          className="flex-1 bg-bg-card border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-brand-cyan/40"
        />
        {format && <span className="text-xs text-ink-3 w-12">{format(value)}</span>}
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="card text-center py-8 text-ink-3 text-sm">{msg}</div>
  );
}
